import { useUser } from "@clerk/clerk-react";
import { zodResolver } from "@hookform/resolvers/zod";
import type { ColumnDef, OnChangeFn, PaginationState, SortingState } from "@tanstack/react-table";
import { format, parseISO, setHours, setMinutes, startOfDay } from "date-fns";
import {
  AlertCircle,
  Archive,
  ArchiveRestore,
  Calendar as CalendarIcon,
  Check,
  Copy,
  CopyPlus,
  Loader2,
  Lock,
  MoreVertical,
  Plus,
  Unlock,
  X,
} from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { type Control, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import { ScheduleModal } from "@/components/admin/AssignmentListModal";
import { DataTable } from "@/components/admin/DataTable";
import { ConversionCount, DeskBadge, FreemiumBadge } from "@/components/admin/FreemiumBadge";
import { SchedulePreview } from "@/components/admin/SchedulePreview";
import { SearchInput } from "@/components/admin/SearchInput";
import { SegmentedTabs, SegTab } from "@/components/admin/SegmentedTabs";
import { StatusBadge } from "@/components/admin/StatusBadge";
import { Calendar } from "@/components/ui/calendar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  type ProgrammeView,
  useAddCohortAssessor,
  useCohortAssessors,
  useCreateProgramme,
  useDeleteProgramme,
  useDuplicateProgramme,
  useEligibleAssessors,
  useLockProgramme,
  useProgrammeLockReadiness,
  useProgrammes,
  useRemoveCohortAssessor,
  useUnlockProgramme,
  useUpdateProgramme,
} from "@/hooks/admin/programmes";
import { useDevFeatures } from "@/hooks/useDevFeatures";
import { AdminLayout } from "@/layouts/AdminLayout";
import { APP_TIME_ZONE } from "@/lib/appTime";
import { isSuperadminRole } from "@/lib/roles";
import { cn } from "@/lib/utils";
import type { ProductCode, Programme } from "@/types";
import { formatCurrency } from "@/utils/payment";
import { type NumberedRow, numberWindows, seedSchedule } from "@/utils/schedule";

function buildInviteLink(programmeId: string): string {
  const base = import.meta.env.VITE_APP_URL || window.location.origin;
  return `${base.replace(/\/+$/, "")}/register?programme_id=${programmeId}`;
}

/** Seeded schedule preview: mirrors the backend seed (start + D + N + open time). */
function generateSchedulePreview(
  startDate: Date,
  durationDays: number,
  count: number,
  openTime: string
): NumberedRow[] {
  // Anchor at the chosen wall-clock open time (local, illustrative). The real
  // schedule is anchored in the app timezone server-side.
  const [hour, minute] = openTime.split(":").map(Number);
  const anchor = setMinutes(setHours(startDate, hour || 0), minute || 0);
  return numberWindows(seedSchedule(anchor, durationDays, count));
}

// ---------------------------------------------------------------------------
// Create form
// ---------------------------------------------------------------------------

/** Submission attempts a new cohort gets, mirroring the API's
 *  DEFAULT_MAX_SUBMISSIONS. Cohorts created before KC-451 ran two. */
const DEFAULT_MAX_SUBMISSIONS = 3;

const PRODUCT_CODES = [
  "dominion",
  "dominion_free",
  "desk",
] as const satisfies readonly ProductCode[];
const PRODUCT_LABELS: Record<ProductCode, string> = {
  dominion: "Dominion",
  dominion_free: "Dominion Free",
  desk: "The Desk",
};

/** The Cohort's Product; older payloads only carry `is_freemium`. */
function programmeProduct(programme: Programme): ProductCode {
  return programme.product ?? (programme.is_freemium ? "dominion_free" : "dominion");
}

/** Freemium and Desk cohorts are always £0 (KC-495, KC-507). */
function isFreeProduct(product: ProductCode): boolean {
  return product !== "dominion";
}

function freePriceNote(product: ProductCode): string | null {
  if (product === "dominion_free") return `Freemium cohorts are always free (${formatCurrency(0)})`;
  if (product === "desk") return `Desk cohorts are always free (${formatCurrency(0)})`;
  return null;
}

const createFormSchema = z.object({
  name: z.string().trim().min(1, "Cohort name is required"),
  // Stored as an ISO date string (YYYY-MM-DD); empty = not yet chosen.
  start_date: z.string().min(1, "Select a start date"),
  // Schedule seed: duration D (days each assignment is open→due) + count N.
  assignment_duration_days: z.number().int().min(1).max(90),
  assignment_count: z.number().int().min(1, "At least one assignment"),
  // Wall-clock time (HH:MM, 24h, app timezone) assignments open and close.
  open_time: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "Enter a valid 24-hour time"),
  is_draft: z.boolean(),
  // Invite-only: excluded from the /available listing, reachable via direct link (KC-389).
  is_private: z.boolean(),
  // The Product the Cohort sells (KC-495, KC-507).
  product: z.enum(PRODUCT_CODES),
  // Price in pounds (converted to cents on submit).
  price_pounds: z.number().min(0),
  // Empty string = unlimited.
  seats_limit: z.string().trim(),
  // Submission attempts per work package, first submission included (KC-451).
  max_submissions: z.number().int().min(1, "At least one submission"),
});
type CreateFormValues = z.infer<typeof createFormSchema>;

const EMPTY_CREATE: CreateFormValues = {
  name: "",
  start_date: "",
  assignment_duration_days: 3,
  assignment_count: 8,
  open_time: "12:00",
  is_draft: true,
  is_private: false,
  product: "dominion",
  price_pounds: 0,
  seats_limit: "",
  max_submissions: DEFAULT_MAX_SUBMISSIONS,
};

function toIsoDate(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
    date.getDate()
  ).padStart(2, "0")}`;
}

function StartDateField({
  control,
  setError,
}: {
  control: Control<CreateFormValues>;
  setError: (msg: string | null) => void;
}) {
  const { devFeaturesEnabled } = useDevFeatures();
  const [calendarOpen, setCalendarOpen] = useState(false);

  const today = startOfDay(new Date());

  // Any weekday is allowed (the backend supports mid-week starts). Only past
  // dates are blocked, and dev mode lifts even that.
  const disabledDays = (date: Date) => {
    if (devFeaturesEnabled) return false;
    return startOfDay(date) < today;
  };

  return (
    <FormField
      control={control}
      name="start_date"
      render={({ field }) => {
        const selectedDate = field.value ? parseISO(field.value) : undefined;
        return (
          <FormItem>
            <FormLabel>Start Date</FormLabel>
            <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className={cn(
                    "flex w-full items-center gap-3 rounded-lg border border-gray-300 px-4 py-2.5 text-left outline-none focus:border-primary focus:ring-2 focus:ring-ring",
                    selectedDate ? "text-gray-900" : "text-gray-400"
                  )}
                >
                  <CalendarIcon className="h-5 w-5 text-gray-400" />
                  {selectedDate
                    ? format(selectedDate, "EEEE, MMMM d, yyyy")
                    : "Select a start date"}
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={(date) => {
                    if (date) {
                      field.onChange(toIsoDate(date));
                      setError(null);
                      setCalendarOpen(false);
                    }
                  }}
                  disabled={disabledDays}
                  defaultMonth={today}
                />
              </PopoverContent>
            </Popover>
            <p className="mt-1 text-sm text-gray-500">
              Assignments open and close at the chosen time. Any weekday — a mid-week start makes
              the first week partial.
              {devFeaturesEnabled && " • Past dates allowed in dev mode"}
            </p>
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
}

function CreateProgrammeModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (programme: Programme) => void;
}) {
  const [submitError, setSubmitError] = useState<string | null>(null);
  const createProgramme = useCreateProgramme();
  const form = useForm<CreateFormValues>({
    resolver: zodResolver(createFormSchema),
    defaultValues: EMPTY_CREATE,
  });
  const { control } = form;
  const isSubmitting = form.formState.isSubmitting;
  // Freemium and Desk cohorts are always free; the API rejects a price with 422.
  const product = form.watch("product");

  const startDateStr = form.watch("start_date");
  const durationDays = form.watch("assignment_duration_days");
  const assignmentCount = form.watch("assignment_count");
  const openTime = form.watch("open_time");
  const startDate = startDateStr ? parseISO(startDateStr) : null;
  const schedulePreview =
    startDate && durationDays >= 1 && assignmentCount >= 1 && /^\d{1,2}:\d{2}$/.test(openTime)
      ? generateSchedulePreview(startDate, durationDays, assignmentCount, openTime)
      : [];
  const totalWeeksPreview = schedulePreview.reduce((max, row) => Math.max(max, row.week), 0);

  const onSubmit = form.handleSubmit(async (values) => {
    setSubmitError(null);
    try {
      const parsedSeats = values.seats_limit.trim() === "" ? null : Number(values.seats_limit);
      const created = await createProgramme.mutateAsync({
        name: values.name.trim(),
        start_date: values.start_date,
        assignment_duration_days: values.assignment_duration_days,
        assignment_count: values.assignment_count,
        open_time: values.open_time,
        is_draft: values.is_draft,
        is_private: values.is_private,
        product: values.product,
        price: isFreeProduct(values.product) ? 0 : Math.round(values.price_pounds * 100),
        seats_limit: parsedSeats,
        max_submissions: values.max_submissions,
      });
      onCreated(created);
      onClose();
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : "Failed to create programme");
    }
  });

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="max-h-[90vh] w-full max-w-2xl overflow-hidden rounded-2xl bg-white shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 p-6">
          <h2 className="text-xl font-semibold text-gray-900">Create New Cohort</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 transition-colors hover:bg-gray-100"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <Form {...form}>
          <form onSubmit={onSubmit} className="max-h-[calc(90vh-140px)] overflow-y-auto p-6">
            {submitError && (
              <div className="mb-4 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-red-700">
                <AlertCircle className="h-5 w-5 flex-shrink-0" />
                <span className="text-sm">{submitError}</span>
              </div>
            )}

            <div className="space-y-6">
              {/* Cohort Name */}
              <FormField
                control={control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Cohort Name</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        className="border-gray-300 bg-white"
                        placeholder="e.g., January 2025 Cohort"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Start Date */}
              <StartDateField control={control} setError={setSubmitError} />

              {/* Schedule seed: duration (D) + count (N) */}
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={control}
                  name="assignment_duration_days"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Assignment duration (days)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min="1"
                          max="90"
                          step="1"
                          value={field.value}
                          onChange={(e) => field.onChange(Number(e.target.value || "0"))}
                        />
                      </FormControl>
                      <p className="mt-1 text-sm text-gray-500">
                        Days each assignment is open → due
                      </p>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={control}
                  name="assignment_count"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Number of assignments</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min="1"
                          step="1"
                          value={field.value}
                          onChange={(e) => field.onChange(Number(e.target.value || "0"))}
                        />
                      </FormControl>
                      <p className="mt-1 text-sm text-gray-500">
                        {Math.floor(7 / Math.max(durationDays, 1))} per week · {totalWeeksPreview}{" "}
                        {totalWeeksPreview === 1 ? "week" : "weeks"}
                      </p>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Opens & closes at (24h, app timezone) */}
              <FormField
                control={control}
                name="open_time"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Opens &amp; closes at</FormLabel>
                    <FormControl>
                      <Input type="time" step="60" className="w-40" {...field} />
                    </FormControl>
                    <p className="mt-1 text-sm text-gray-500">
                      24-hour, {APP_TIME_ZONE.replace("_", " ")}. Assignments open and close at this
                      time — one uniform time keeps windows back-to-back.
                    </p>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Schedule preview — directly under the seed inputs */}
              {startDate && schedulePreview.length > 0 && (
                <div>
                  <span className="mb-2 block text-sm font-medium text-gray-700">
                    Assignment schedule preview
                  </span>
                  <SchedulePreview rows={schedulePreview} timeZone="local" />
                </div>
              )}

              {/* Price */}
              <FormField
                control={control}
                name="price_pounds"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Price</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500">
                          £
                        </span>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          className="border-gray-300 bg-white pl-8"
                          placeholder="0.00"
                          aria-label="Price"
                          value={isFreeProduct(product) ? 0 : field.value}
                          disabled={isFreeProduct(product)}
                          onChange={(e) => field.onChange(parseFloat(e.target.value || "0"))}
                        />
                      </div>
                    </FormControl>
                    <p className="mt-1 text-sm text-gray-500">
                      {freePriceNote(product) ?? `Set to ${formatCurrency(0)} for free programmes`}
                    </p>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Seat limit */}
              <FormField
                control={control}
                name="seats_limit"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Seat limit</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min="1"
                        step="1"
                        className="border-gray-300 bg-white"
                        placeholder="Leave blank for unlimited"
                        {...field}
                      />
                    </FormControl>
                    <p className="mt-1 text-sm text-gray-500">
                      Maximum number of active enrollments. Leave blank for unlimited.
                    </p>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Submissions per work package (KC-451) */}
              <FormField
                control={control}
                name="max_submissions"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Submissions per work package</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min="1"
                        step="1"
                        className="border-gray-300 bg-white"
                        value={field.value}
                        onChange={(e) => field.onChange(parseInt(e.target.value || "1", 10))}
                      />
                    </FormControl>
                    <p className="mt-1 text-sm text-gray-500">
                      How many times a participant may submit each work package, the first
                      submission included.
                    </p>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Publish Status */}
              <FormField
                control={control}
                name="is_draft"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Status</FormLabel>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => field.onChange(true)}
                        className={cn(
                          "flex-1 rounded-lg border px-4 py-2.5 font-medium transition-colors",
                          field.value
                            ? "border-gray-400 bg-gray-100 text-gray-900"
                            : "border-gray-300 bg-white text-gray-500 hover:bg-gray-50"
                        )}
                      >
                        Draft
                      </button>
                      <button
                        type="button"
                        onClick={() => field.onChange(false)}
                        className={cn(
                          "flex-1 rounded-lg border px-4 py-2.5 font-medium transition-colors",
                          !field.value
                            ? "border-green-400 bg-green-100 text-green-800"
                            : "border-gray-300 bg-white text-gray-500 hover:bg-gray-50"
                        )}
                      >
                        Published
                      </button>
                    </div>
                    <p className="mt-1 text-sm text-gray-500">
                      Draft programmes are not visible to users
                    </p>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Visibility */}
              <FormField
                control={control}
                name="is_private"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Visibility</FormLabel>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        aria-pressed={!field.value}
                        onClick={() => field.onChange(false)}
                        className={cn(
                          "flex-1 rounded-lg border px-4 py-2.5 font-medium transition-colors",
                          !field.value
                            ? "border-green-400 bg-green-100 text-green-800"
                            : "border-gray-300 bg-white text-gray-500 hover:bg-gray-50"
                        )}
                      >
                        Public
                      </button>
                      <button
                        type="button"
                        aria-pressed={field.value}
                        onClick={() => field.onChange(true)}
                        className={cn(
                          "flex-1 rounded-lg border px-4 py-2.5 font-medium transition-colors",
                          field.value
                            ? "border-amber-400 bg-amber-100 text-amber-800"
                            : "border-gray-300 bg-white text-gray-500 hover:bg-gray-50"
                        )}
                      >
                        Private
                      </button>
                    </div>
                    <p className="mt-1 text-sm text-gray-500">
                      Private cohorts are invite-only — never listed as available. Only reachable by
                      sharing a direct link.
                    </p>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={control}
                name="product"
                render={({ field }) => (
                  <ProductField
                    id="create-product"
                    value={field.value}
                    onChange={(next) => {
                      field.onChange(next);
                      if (isFreeProduct(next)) form.setValue("price_pounds", 0);
                      // Desk cohorts are invite-only by default (KC-507).
                      if (next === "desk") form.setValue("is_private", true);
                    }}
                  />
                )}
              />
            </div>

            {/* Footer */}
            <div className="mt-6 flex justify-end gap-3 border-t border-gray-200 pt-6">
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg px-4 py-2 text-gray-700 transition-colors hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-white transition-colors hover:bg-primary/90 disabled:bg-primary/40"
              >
                {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
                Create Cohort
              </button>
            </div>
          </form>
        </Form>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Edit form
// ---------------------------------------------------------------------------

const editFormSchema = z.object({
  name: z.string().trim().min(1, "Name is required"),
  price_pounds: z.number().min(0),
  seats_limit: z.string().trim(),
  // Submission attempts per work package, first submission included (KC-451).
  max_submissions: z.number().int().min(1, "At least one submission"),
  is_draft: z.boolean(),
  is_private: z.boolean(),
  product: z.enum(PRODUCT_CODES),
});
type EditFormValues = z.infer<typeof editFormSchema>;

const PRODUCT_HINTS: Record<ProductCode, string> = {
  dominion: "Dominion: the paid programme with every extra and a certificate.",
  dominion_free:
    "Freemium cohorts sell Dominion Free: no live portfolio or extras, and a participation badge instead of a certificate.",
  desk: "The Desk: professionals write a review on a brief after completing their profile. Free, no certificate or badge.",
};

/** The Product select: which Product the Cohort sells (KC-495, KC-507). */
function ProductField({
  id,
  value,
  onChange,
  disabled = false,
}: {
  id: string;
  value: ProductCode;
  onChange: (value: ProductCode) => void;
  disabled?: boolean;
}) {
  return (
    <FormItem>
      <Label htmlFor={id}>Product</Label>
      <select
        id={id}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value as ProductCode)}
        className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-gray-900 outline-none focus:border-primary focus:ring-2 focus:ring-ring disabled:opacity-60"
      >
        {PRODUCT_CODES.map((code) => (
          <option key={code} value={code}>
            {PRODUCT_LABELS[code]}
          </option>
        ))}
      </select>
      <p className="mt-1 text-sm text-gray-500">
        {disabled
          ? "Someone has enrolled, so this cohort can't switch Product."
          : PRODUCT_HINTS[value]}
      </p>
    </FormItem>
  );
}

function AssessorsSection({ programmeId }: { programmeId: string }) {
  const [selectedUserId, setSelectedUserId] = useState("");
  const assessorsQuery = useCohortAssessors(programmeId);
  const eligibleQuery = useEligibleAssessors(programmeId);
  const addAssessor = useAddCohortAssessor(programmeId);
  const removeAssessor = useRemoveCohortAssessor(programmeId);

  const assessors = assessorsQuery.data ?? [];
  const eligible = eligibleQuery.data ?? [];

  function initials(name: string) {
    return name
      .split(" ")
      .map((w) => w[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  }

  async function handleAdd() {
    if (!selectedUserId) return;
    await addAssessor.mutateAsync(selectedUserId);
    setSelectedUserId("");
  }

  return (
    <div className="mt-6 border-t border-gray-200 pt-5">
      <div className="mb-1 flex items-center gap-1.5">
        <svg
          width="14"
          height="14"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          viewBox="0 0 24 24"
          className="text-gray-500"
        >
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
        <span className="text-sm font-medium text-gray-700">Assessors</span>
      </div>
      <p className="mb-3 text-xs text-gray-500">
        Assessors receive an email each time a submission is AI-reviewed and ready for human
        assessment.
      </p>

      {assessors.length === 0 && !assessorsQuery.isLoading && (
        <p className="mb-3 rounded-lg border border-dashed border-gray-300 bg-gray-50 px-4 py-3 text-center text-sm text-gray-400">
          No assessors assigned yet
        </p>
      )}

      {assessors.length > 0 && (
        <div className="mb-3 flex flex-col gap-2">
          {assessors.map((a) => (
            <div
              key={a.user_id}
              className="flex items-center gap-2.5 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2"
            >
              <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-indigo-100 text-xs font-bold text-indigo-700">
                {initials(a.first_name ?? a.username)}
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-gray-900">
                  {a.first_name ? `${a.first_name} (${a.username})` : a.username}
                </p>
                {a.email && <p className="truncate text-xs text-gray-500">{a.email}</p>}
              </div>
              <button
                type="button"
                onClick={() => removeAssessor.mutate(a.user_id)}
                disabled={removeAssessor.isPending}
                className="flex-shrink-0 rounded p-0.5 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-500 disabled:opacity-50"
                title="Remove assessor"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="flex gap-2">
        <select
          value={selectedUserId}
          onChange={(e) => setSelectedUserId(e.target.value)}
          disabled={eligible.length === 0 || eligibleQuery.isLoading}
          className="min-w-0 flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900 disabled:opacity-50"
        >
          <option value="">
            {eligibleQuery.isLoading
              ? "Loading…"
              : eligible.length === 0
                ? assessors.length > 0
                  ? "All eligible users assigned"
                  : "No admin or assessor users found"
                : "Add an assessor…"}
          </option>
          {eligible.map((u) => (
            <option key={u.id} value={u.id}>
              {u.first_name ? `${u.first_name} (${u.username})` : u.username}
              {u.email ? ` — ${u.email}` : ""}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={handleAdd}
          disabled={!selectedUserId || addAssessor.isPending}
          className="flex-shrink-0 rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-gray-700 disabled:opacity-40"
        >
          {addAssessor.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add"}
        </button>
      </div>
      <p className="mt-1.5 text-xs text-gray-400">
        Only admin and assessor role users can be added.
      </p>
    </div>
  );
}

function EditProgrammeModal({ programme, onClose }: { programme: Programme; onClose: () => void }) {
  const [submitError, setSubmitError] = useState<string | null>(null);
  const updateProgramme = useUpdateProgramme();
  const form = useForm<EditFormValues>({
    resolver: zodResolver(editFormSchema),
    defaultValues: {
      name: programme.name,
      price_pounds: programme.price / 100,
      seats_limit: programme.seats_limit != null ? String(programme.seats_limit) : "",
      max_submissions: programme.max_submissions ?? DEFAULT_MAX_SUBMISSIONS,
      is_draft: programme.is_draft,
      is_private: programme.is_private,
      product: programmeProduct(programme),
    },
  });
  const { control } = form;
  const isSubmitting = form.formState.isSubmitting;
  // Freemium and Desk cohorts are always free; the API rejects a price with 422.
  const product = form.watch("product");

  const activeCount = programme.enrollment_count ?? 0;
  // The Product decides what enrolled Participants get, so it is fixed once
  // anyone has enrolled; the API refuses the change with a 409 (KC-495).
  const productLocked = (programme.enrollment_count ?? 0) > 0;
  const seatsLimitInput = form.watch("seats_limit");
  const parsedSeatsForWarning = seatsLimitInput.trim() === "" ? null : Number(seatsLimitInput);
  const showLowerWarning = parsedSeatsForWarning != null && parsedSeatsForWarning < activeCount;

  const onSubmit = form.handleSubmit(async (values) => {
    setSubmitError(null);
    try {
      const parsedSeats = values.seats_limit.trim() === "" ? null : Number(values.seats_limit);
      await updateProgramme.mutateAsync({
        id: programme.id,
        data: {
          name: values.name.trim(),
          is_draft: values.is_draft,
          is_private: values.is_private,
          ...(productLocked ? {} : { product: values.product }),
          price: isFreeProduct(values.product) ? 0 : Math.round(values.price_pounds * 100),
          seats_limit: parsedSeats,
          max_submissions: values.max_submissions,
        },
      });
      onClose();
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : "Failed to update programme");
    }
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl">
        <div className="mb-5 flex items-start justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Edit programme</h2>
            <p className="mt-1 text-sm text-gray-500">
              Schedule (start date, weeks) can't be changed after creation.
            </p>
          </div>
        </div>

        {submitError && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-2.5 text-sm text-red-700">
            {submitError}
          </div>
        )}

        <Form {...form}>
          <form onSubmit={onSubmit} className="space-y-4">
            <FormField
              control={control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={control}
              name="price_pounds"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Price</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500">
                        £
                      </span>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        className="pl-8"
                        placeholder="0.00"
                        aria-label="Price"
                        value={isFreeProduct(product) ? 0 : field.value}
                        disabled={isFreeProduct(product)}
                        onChange={(e) => field.onChange(parseFloat(e.target.value || "0"))}
                      />
                    </div>
                  </FormControl>
                  {isFreeProduct(product) && (
                    <p className="mt-1 text-sm text-gray-500">{freePriceNote(product)}</p>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={control}
              name="seats_limit"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Seat limit</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min="1"
                      step="1"
                      placeholder="Leave blank for unlimited"
                      {...field}
                    />
                  </FormControl>
                  <p className="mt-1 text-sm text-gray-500">
                    Maximum number of active enrollments. Leave blank for unlimited.
                  </p>
                  {showLowerWarning && (
                    <p className="mt-2 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700">
                      {activeCount} {activeCount === 1 ? "user is" : "users are"} currently
                      enrolled. Saving with a lower limit blocks new joins until someone drops or
                      you raise the limit again. Existing enrollments stay.
                    </p>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Submissions per work package (KC-451) */}
            <FormField
              control={control}
              name="max_submissions"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Submissions per work package</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min="1"
                      step="1"
                      value={field.value}
                      onChange={(e) => field.onChange(parseInt(e.target.value || "1", 10))}
                    />
                  </FormControl>
                  <p className="mt-1 text-sm text-gray-500">
                    How many times a participant may submit each work package, the first submission
                    included. Changing this applies to attempts not yet used — submissions already
                    made are never withdrawn.
                  </p>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={control}
              name="is_draft"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Status</FormLabel>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => field.onChange(true)}
                      className={cn(
                        "flex-1 rounded-lg border px-4 py-2.5 font-medium transition-colors",
                        field.value
                          ? "border-gray-400 bg-gray-100 text-gray-900"
                          : "border-gray-300 bg-white text-gray-500 hover:bg-gray-50"
                      )}
                    >
                      Draft
                    </button>
                    <button
                      type="button"
                      onClick={() => field.onChange(false)}
                      className={cn(
                        "flex-1 rounded-lg border px-4 py-2.5 font-medium transition-colors",
                        !field.value
                          ? "border-green-400 bg-green-100 text-green-800"
                          : "border-gray-300 bg-white text-gray-500 hover:bg-gray-50"
                      )}
                    >
                      Published
                    </button>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={control}
              name="is_private"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Visibility</FormLabel>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      aria-pressed={!field.value}
                      onClick={() => field.onChange(false)}
                      className={cn(
                        "flex-1 rounded-lg border px-4 py-2.5 font-medium transition-colors",
                        !field.value
                          ? "border-green-400 bg-green-100 text-green-800"
                          : "border-gray-300 bg-white text-gray-500 hover:bg-gray-50"
                      )}
                    >
                      Public
                    </button>
                    <button
                      type="button"
                      aria-pressed={field.value}
                      onClick={() => field.onChange(true)}
                      className={cn(
                        "flex-1 rounded-lg border px-4 py-2.5 font-medium transition-colors",
                        field.value
                          ? "border-amber-400 bg-amber-100 text-amber-800"
                          : "border-gray-300 bg-white text-gray-500 hover:bg-gray-50"
                      )}
                    >
                      Private
                    </button>
                  </div>
                  <p className="mt-1 text-sm text-gray-500">
                    Private cohorts are invite-only — never listed as available. Only reachable by
                    sharing a direct link.
                  </p>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={control}
              name="product"
              render={({ field }) => (
                <ProductField
                  id="edit-product"
                  value={field.value}
                  onChange={(next) => {
                    field.onChange(next);
                    if (isFreeProduct(next)) form.setValue("price_pounds", 0);
                  }}
                  disabled={productLocked}
                />
              )}
            />

            <AssessorsSection programmeId={programme.id} />

            <div className="flex justify-end gap-2 border-t border-gray-200 pt-4">
              <button
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
                className="rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium hover:bg-gray-200 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-2 text-sm font-semibold text-white hover:bg-primary/90 disabled:opacity-50"
              >
                {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
                Save changes
              </button>
            </div>
          </form>
        </Form>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Status pill helpers
// ---------------------------------------------------------------------------

function statusTone(status: string): "info" | "success" | "neutral" {
  switch (status.toLowerCase()) {
    case "in_progress":
      return "info";
    case "completed":
      return "success";
    default:
      return "neutral";
  }
}

function formatStatus(status: string): string {
  return status.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

/** A programme is archived when it has an archived_at timestamp. */
function isArchived(programme: Programme): boolean {
  return programme.is_archived ?? programme.archived_at != null;
}

/** A cohort is locked once it has a locked_at timestamp (KC-287). */
function isLocked(programme: Programme): boolean {
  return programme.is_locked ?? programme.locked_at != null;
}

const VIEW_TABS: { id: ProgrammeView; label: string }[] = [
  { id: "active", label: "Active" },
  { id: "completed", label: "Completed" },
  { id: "archived", label: "Archived" },
  { id: "all", label: "All" },
];

const PAGE_SIZE = 25;

/**
 * Lock-cohort confirmation dialog (KC-287). Renders the server-computed readiness
 * checklist green/red per item and only enables Confirm once every check passes.
 */
function LockCohortDialog({ programme, onClose }: { programme: Programme; onClose: () => void }) {
  const readinessQuery = useProgrammeLockReadiness(programme.id);
  const lockProgramme = useLockProgramme();
  const [error, setError] = useState<string | null>(null);
  const readiness = readinessQuery.data;

  const handleConfirm = () => {
    setError(null);
    lockProgramme.mutate(programme.id, {
      onSuccess: onClose,
      onError: (err) => setError(err instanceof Error ? err.message : "Failed to lock cohort"),
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
        <div className="mb-4 flex items-start gap-4">
          <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-green-100">
            <Lock className="h-6 w-6 text-green-700" />
          </div>
          <div className="flex-1">
            <h3 className="mb-1 text-lg font-semibold text-gray-900">Lock Cohort</h3>
            <p className="text-sm text-gray-600">
              Locking <span className="font-medium">"{programme.name}"</span> freezes topic
              assignments and reveals topics to students. Every check must pass first.
            </p>
          </div>
        </div>

        <div className="mb-6 space-y-2">
          {readinessQuery.isPending && (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Loader2 className="h-4 w-4 animate-spin" /> Checking readiness…
            </div>
          )}
          {readiness?.items.map((item) => (
            <div key={item.key} className="flex items-start gap-2">
              {item.passed ? (
                <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-green-600" />
              ) : (
                <X className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-600" />
              )}
              <div className="text-sm">
                <div className={item.passed ? "text-gray-900" : "text-red-700"}>{item.label}</div>
                <div className="text-gray-500">{item.detail}</div>
              </div>
            </div>
          ))}
        </div>

        {error && (
          <div className="mb-4 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            {error}
          </div>
        )}

        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={lockProgramme.isPending}
            className="rounded-lg px-4 py-2 text-gray-700 transition-colors hover:bg-gray-100 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!readiness?.ready || lockProgramme.isPending}
            className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-white transition-colors hover:bg-primary/90 disabled:opacity-50"
          >
            {lockProgramme.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Lock Cohort
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminProgrammesPage() {
  const navigate = useNavigate();
  const { user } = useUser();
  const isSuperadmin = isSuperadminRole(user?.publicMetadata?.role as string | undefined);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [sorting, setSorting] = useState<SortingState>([]);
  const [view, setView] = useState<ProgrammeView>("active");

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [deletingProgramme, setDeletingProgramme] = useState<Programme | null>(null);
  const [editingProgramme, setEditingProgramme] = useState<Programme | null>(null);
  const [managingSchedule, setManagingSchedule] = useState<Programme | null>(null);
  const [archivingProgramme, setArchivingProgramme] = useState<Programme | null>(null);
  const [lockingProgramme, setLockingProgramme] = useState<Programme | null>(null);
  const [copiedLinkId, setCopiedLinkId] = useState<string | null>(null);

  const handleCopyInviteLink = useCallback((programme: Programme) => {
    navigator.clipboard.writeText(buildInviteLink(programme.id));
    setCopiedLinkId(programme.id);
    setTimeout(() => {
      setCopiedLinkId((current) => (current === programme.id ? null : current));
    }, 1500);
  }, []);

  const sort = sorting[0];
  const programmesQuery = useProgrammes({
    page,
    page_size: PAGE_SIZE,
    q: search || undefined,
    sort_by: sort?.id,
    sort_order: sort ? (sort.desc ? "desc" : "asc") : undefined,
    view,
  });
  const data = programmesQuery.data ?? null;
  const error = programmesQuery.isError
    ? programmesQuery.error instanceof Error
      ? programmesQuery.error.message
      : "Failed to load programmes"
    : null;

  const deleteProgramme = useDeleteProgramme();
  const { mutateAsync: mutateDeleteProgramme, isPending: isDeleting } = deleteProgramme;
  const {
    mutate: mutateUpdateProgramme,
    isPending: isTogglingDraft,
    variables: togglingVariables,
  } = useUpdateProgramme();
  const togglingDraft = isTogglingDraft ? togglingVariables?.id : null;

  const { mutate: mutateDuplicate, isPending: isDuplicating } = useDuplicateProgramme();
  const handleDuplicate = useCallback(
    (programme: Programme) => {
      mutateDuplicate(programme.id, {
        onSuccess: (draft) => navigate(`/admin/programmes/${draft.id}/users`),
      });
    },
    [mutateDuplicate, navigate]
  );

  const handleDeleteProgramme = async () => {
    if (!deletingProgramme) return;
    try {
      await mutateDeleteProgramme(deletingProgramme.id);
      setDeletingProgramme(null);
    } catch (err) {
      console.error("Failed to delete programme:", err);
    }
  };

  const handleToggleDraft = useCallback(
    (programme: Programme) => {
      mutateUpdateProgramme({ id: programme.id, data: { is_draft: !programme.is_draft } });
    },
    [mutateUpdateProgramme]
  );

  // Unarchiving is immediate (reversible, low-risk); archiving is confirmed below.
  const handleUnarchive = useCallback(
    (programme: Programme) => {
      mutateUpdateProgramme({ id: programme.id, data: { is_archived: false } });
    },
    [mutateUpdateProgramme]
  );

  const handleConfirmArchive = () => {
    if (!archivingProgramme) return;
    mutateUpdateProgramme(
      { id: archivingProgramme.id, data: { is_archived: true } },
      { onSuccess: () => setArchivingProgramme(null) }
    );
  };

  // Unlocking is immediate and superadmin-only (the endpoint enforces the role);
  // locking is gated behind the readiness dialog below.
  const unlockProgramme = useUnlockProgramme();
  const { mutate: mutateUnlock, isPending: isUnlocking, variables: unlockingId } = unlockProgramme;
  const handleUnlock = useCallback(
    (programme: Programme) => {
      mutateUnlock(programme.id);
    },
    [mutateUnlock]
  );

  const columns = useMemo<ColumnDef<Programme>[]>(
    () => [
      {
        accessorKey: "name",
        header: "Name",
        cell: ({ row }) => {
          const programme = row.original;
          return (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => navigate(`/admin/programmes/${programme.id}/users`)}
                className="font-medium text-gray-900 transition-colors hover:text-green-600"
              >
                {programme.name}
              </button>
              <StatusBadge tone={programme.is_draft ? "neutral" : "success"}>
                {programme.is_draft ? "Draft" : "Published"}
              </StatusBadge>
              {programme.is_private && (
                <span title="Invite-only — not listed as available">
                  <StatusBadge tone="warning">Private</StatusBadge>
                </span>
              )}
              {programmeProduct(programme) === "dominion_free" && <FreemiumBadge />}
              {programmeProduct(programme) === "desk" && <DeskBadge />}
              {isArchived(programme) && <StatusBadge tone="warning">Archived</StatusBadge>}
              {isLocked(programme) && (
                <span title="Cohort locked" className="text-gray-500">
                  <Lock className="h-4 w-4" aria-label="Cohort locked" />
                </span>
              )}
            </div>
          );
        },
      },
      {
        accessorKey: "status",
        header: "Status",
        enableSorting: false,
        cell: ({ row }) => (
          <StatusBadge tone={statusTone(row.original.status)}>
            {formatStatus(row.original.status)}
          </StatusBadge>
        ),
      },
      {
        accessorKey: "start_date",
        header: "Dates",
        cell: ({ row }) => (
          <span className="text-sm text-gray-500">
            {format(new Date(row.original.start_date), "MMM d")} -{" "}
            {format(new Date(row.original.end_date), "MMM d, yyyy")}
          </span>
        ),
      },
      {
        accessorKey: "total_weeks",
        header: "Weeks",
        cell: ({ row }) => (
          <span className="text-sm text-gray-500">{row.original.total_weeks}</span>
        ),
      },
      {
        id: "enrolled",
        header: "Enrolled",
        enableSorting: false,
        cell: ({ row }) => (
          <div className="text-sm text-gray-500">
            {row.original.enrollment_count ?? 0}
            {row.original.conversion && (
              <div className="text-xs text-gray-400">
                <ConversionCount conversion={row.original.conversion} />
              </div>
            )}
          </div>
        ),
      },
      {
        id: "capacity",
        header: "Capacity",
        enableSorting: false,
        cell: ({ row }) => {
          const p = row.original;
          return (
            <span className="text-sm text-gray-500">
              {p.seats_limit != null
                ? `${p.seats_limit - (p.seats_remaining ?? 0)}/${p.seats_limit}`
                : "Unlimited"}
            </span>
          );
        },
      },
      {
        accessorKey: "price",
        header: "Price",
        cell: ({ row }) => (
          <span className="text-sm text-gray-500">
            {row.original.price > 0 ? formatCurrency(row.original.price) : "FREE"}
          </span>
        ),
      },
      {
        accessorKey: "created_at",
        header: "Created",
        cell: ({ row }) => (
          <span className="text-sm text-gray-500">
            {format(new Date(row.original.created_at), "MMM d, yyyy")}
          </span>
        ),
      },
      {
        id: "actions",
        header: "",
        enableSorting: false,
        cell: ({ row }) => {
          const programme = row.original;
          return (
            <div className="text-right">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="rounded-lg p-2 transition-colors hover:bg-gray-100">
                    <MoreVertical className="h-4 w-4 text-gray-500" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    onClick={() => navigate(`/admin/programmes/${programme.id}/users`)}
                  >
                    Manage Users
                  </DropdownMenuItem>
                  {programme.is_private && (
                    <DropdownMenuItem
                      onSelect={(event) => {
                        event.preventDefault();
                        handleCopyInviteLink(programme);
                      }}
                    >
                      {copiedLinkId === programme.id ? (
                        <>
                          <Check className="mr-2 h-4 w-4 text-green-600" />
                          Copied!
                        </>
                      ) : (
                        <>
                          <Copy className="mr-2 h-4 w-4" />
                          Copy invite link
                        </>
                      )}
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={() => setEditingProgramme(programme)}>
                    Edit
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setManagingSchedule(programme)}>
                    Manage schedule
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => handleDuplicate(programme)}
                    disabled={isDuplicating}
                  >
                    <CopyPlus className="mr-2 h-4 w-4" />
                    Duplicate to next Monday
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => handleToggleDraft(programme)}
                    disabled={togglingDraft === programme.id}
                  >
                    {programme.is_draft ? "Publish" : "Mark as Draft"}
                  </DropdownMenuItem>
                  {isArchived(programme) ? (
                    <DropdownMenuItem
                      onClick={() => handleUnarchive(programme)}
                      disabled={togglingDraft === programme.id}
                    >
                      <ArchiveRestore className="mr-2 h-4 w-4" />
                      Unarchive
                    </DropdownMenuItem>
                  ) : (
                    <DropdownMenuItem onClick={() => setArchivingProgramme(programme)}>
                      <Archive className="mr-2 h-4 w-4" />
                      Archive
                    </DropdownMenuItem>
                  )}
                  {isLocked(programme) ? (
                    isSuperadmin && (
                      <DropdownMenuItem
                        onClick={() => handleUnlock(programme)}
                        disabled={isUnlocking && unlockingId === programme.id}
                      >
                        <Unlock className="mr-2 h-4 w-4" />
                        Unlock Cohort
                      </DropdownMenuItem>
                    )
                  ) : (
                    <DropdownMenuItem onClick={() => setLockingProgramme(programme)}>
                      <Lock className="mr-2 h-4 w-4" />
                      Lock Cohort
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem
                    onClick={() => setDeletingProgramme(programme)}
                    className="text-red-600 focus:text-red-600"
                  >
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          );
        },
      },
    ],
    [
      navigate,
      handleDuplicate,
      isDuplicating,
      togglingDraft,
      handleToggleDraft,
      handleUnarchive,
      isSuperadmin,
      handleUnlock,
      isUnlocking,
      unlockingId,
    ]
  );

  const pagination: PaginationState = { pageIndex: page - 1, pageSize: PAGE_SIZE };
  const onPaginationChange: OnChangeFn<PaginationState> = (updater) => {
    const next = typeof updater === "function" ? updater(pagination) : updater;
    setPage(next.pageIndex + 1);
  };
  const onSortingChange: OnChangeFn<SortingState> = (updater) => {
    setSorting((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      setPage(1); // re-sort from the first page
      return next;
    });
  };

  const programmes = data?.items ?? [];

  return (
    <AdminLayout>
      <div className="flex h-full flex-col gap-4">
        <DataTable
          className="h-full"
          columns={columns}
          data={programmes}
          isLoading={programmesQuery.isPending}
          error={error}
          onRetry={() => programmesQuery.refetch()}
          emptyMessage="No programmes found"
          getRowId={(p) => p.id}
          toolbar={
            <div className="flex items-center justify-between gap-3">
              <div className="flex min-w-0 items-center gap-3">
                {/* Lifecycle view tabs */}
                <SegmentedTabs>
                  {VIEW_TABS.map((tab) => (
                    <SegTab
                      key={tab.id}
                      active={view === tab.id}
                      onClick={() => {
                        setView(tab.id);
                        setPage(1);
                      }}
                    >
                      {tab.label}
                    </SegTab>
                  ))}
                </SegmentedTabs>
                <SearchInput
                  value={search}
                  onChange={(v) => {
                    setSearch(v);
                    setPage(1);
                  }}
                  placeholder="Search programmes…"
                />
              </div>
              <button
                onClick={() => setShowCreateModal(true)}
                className="flex flex-shrink-0 items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary/90"
              >
                <Plus className="h-4 w-4" />
                Create Cohort
              </button>
            </div>
          }
          manualPagination
          pageCount={data?.total_pages ?? 0}
          rowCount={data?.total ?? 0}
          pagination={pagination}
          onPaginationChange={onPaginationChange}
          manualSorting
          sorting={sorting}
          onSortingChange={onSortingChange}
        />
      </div>

      {/* Create Modal */}
      <CreateProgrammeModal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreated={(programme) => {
          setPage(1);
          setEditingProgramme(programme);
        }}
      />

      {/* Edit Modal */}
      {editingProgramme && (
        <EditProgrammeModal
          programme={editingProgramme}
          onClose={() => setEditingProgramme(null)}
        />
      )}

      {/* Manage schedule (hybrid window editor) */}
      {managingSchedule && (
        <ScheduleModal
          programme={managingSchedule}
          open
          onClose={() => setManagingSchedule(null)}
        />
      )}

      {/* Archive Confirmation Modal */}
      {archivingProgramme && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
            <div className="mb-4 flex items-start gap-4">
              <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-amber-100">
                <Archive className="h-6 w-6 text-amber-600" />
              </div>
              <div className="flex-1">
                <h3 className="mb-1 text-lg font-semibold text-gray-900">Archive Programme</h3>
                <p className="text-sm text-gray-600">
                  Archive <span className="font-medium">"{archivingProgramme.name}"</span>? It will
                  be hidden from the active list and from users browsing available programmes. No
                  data is deleted, and you can unarchive it at any time.
                </p>
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setArchivingProgramme(null)}
                disabled={isTogglingDraft}
                className="rounded-lg px-4 py-2 text-gray-700 transition-colors hover:bg-gray-100 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmArchive}
                disabled={isTogglingDraft}
                className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-white transition-colors hover:bg-primary/90 disabled:opacity-50"
              >
                {isTogglingDraft && <Loader2 className="h-4 w-4 animate-spin" />}
                Archive Programme
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lock Cohort Modal (readiness checklist) */}
      {lockingProgramme && (
        <LockCohortDialog programme={lockingProgramme} onClose={() => setLockingProgramme(null)} />
      )}

      {/* Delete Confirmation Modal */}
      {deletingProgramme && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
            <div className="mb-4 flex items-start gap-4">
              <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100">
                <AlertCircle className="h-6 w-6 text-red-600" />
              </div>
              <div className="flex-1">
                <h3 className="mb-1 text-lg font-semibold text-gray-900">Delete Programme</h3>
                <p className="text-sm text-gray-600">
                  Are you sure you want to delete{" "}
                  <span className="font-medium">"{deletingProgramme.name}"</span>?
                </p>
              </div>
            </div>

            <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50 p-3">
              <p className="text-sm text-amber-800">
                <strong>Warning:</strong> This programme has{" "}
                <strong>{deletingProgramme.enrollment_count ?? 0}</strong> enrolled user
                {deletingProgramme.enrollment_count !== 1 ? "s" : ""}. This action cannot be undone.
              </p>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeletingProgramme(null)}
                disabled={isDeleting}
                className="rounded-lg px-4 py-2 text-gray-700 transition-colors hover:bg-gray-100 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteProgramme}
                disabled={isDeleting}
                className="flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-white transition-colors hover:bg-red-700 disabled:opacity-50"
              >
                {isDeleting && <Loader2 className="h-4 w-4 animate-spin" />}
                Delete Programme
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
